import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useEffect, useState } from 'react';
import { Button, ClickAwayListener, Loading, Menu, MenuItem, TextField } from '@wayste/sour-ui';
import { autoUpdate, offset, shift, size, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { Portal } from '@headlessui/react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { debounce, max } from 'lodash';
import { Controller, get, useFormContext } from 'react-hook-form';
import { useSourSearchContext } from '../context/use-sour-search-context';
import { NoResults } from './helper/no-results';
import { filterSearchTips } from './helper/search-tip-schema';
import { SearchTips } from './helper/search-tips';
import { SearchResults } from './search-results';
const TIMEOUT = 300;
///////////////////////////////////////////////
// SOUR SEARCH BASE COMPONENT
///////////////////////////////////////////////
export const SourSearch = forwardRef(({ options }, ref) => {
    useEffect(() => {
        if (ref && 'current' in ref && ref.current) {
            ref.current.focus();
        }
    }, [ref]); // Dependency on inputRef so that focus is set correctly
    // DEFAULT OPTIONS
    const { showResults = true, showTips = true, showMessages = true, searchPopoverFixed = false, placeholder = 'Search', searchPositionFixed = false, label = 'Search', textFieldStyle, textFieldDense, onMagnifyingGlassClick, hideClearButton, } = options || {
        showResults: true,
        showTips: true,
        showMessages: true,
        searchPopoverFixed: false,
        placeholder: 'Search',
        searchPositionFixed: false,
        label: 'Search',
        onMagnifyingGlassClick: undefined,
        textFieldStyle: undefined,
        textFieldDense: false,
        hideClearButton: false,
        startingEasyFilter: 'any_field',
    };
    const { onSubmit, results, isLoading, setSearchActive, formInitialized, environmentOptions, defaultFilters, showEasyFilter, searchTerm, setSearchTerm, } = useSourSearchContext();
    const { handleSubmit, setValue, control, watch, formState: { errors }, } = useFormContext();
    const watchQuery = watch('query');
    const watchType = watch('type');
    // LOCAL STATE
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const [filteredItems, setFilteredItems] = useState([]);
    const [filteredItemsType, setFilteredItemsType] = useState(undefined);
    const [initialFormChecked, setInitialFormChecked] = useState(false);
    const [typing, setTyping] = useState(false);
    const [easyFilterOptions, setEasyFilterOptions] = useState([
        { key: 'any_field', hint: 'no restrictions' },
        ...filterSearchTips(environmentOptions.application, defaultFilters?.query?.type),
    ]);
    const [selectedFilterOption, setSelectedFilterOptions] = useState(options?.startingEasyFilter);
    // FLOATING POPOVER
    const { refs, floatingStyles, context } = useFloating({
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
        open: isOpen,
        middleware: [
            offset(5),
            size({
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, {
                        minWidth: `${rects.reference.width}px`,
                        width: options?.matchInputWidth ? `${rects.reference.width}px` : 'auto',
                        maxWidth: options?.matchInputWidth
                            ? `${rects.reference.width}px`
                            : `${max([rects.reference.width, options?.maxWidth || window.innerWidth])}px`,
                    });
                },
            }),
            shift({
                padding: 16, // Ensures there's padding from the edge of the viewport
                boundary: document.body, // Use the body as the boundary
            }),
        ],
    });
    const interactions = [useRole(context, { role: 'select' })];
    const { getReferenceProps, getFloatingProps } = useInteractions(interactions);
    useEffect(() => {
        if (!watchType) {
            setEasyFilterOptions([{ key: 'any_field', hint: 'no restrictions' }]);
            setSelectedFilterOptions('any_field');
        }
        else {
            const newOptions = [
                { key: 'any_field', hint: 'no restrictions' },
                ...filterSearchTips(environmentOptions.application, watchType),
            ];
            setEasyFilterOptions(newOptions);
            const keyExists = newOptions.find((item) => item.key === selectedFilterOption);
            if (!keyExists) {
                setSelectedFilterOptions('any_field');
            }
        }
    }, [watchType]);
    ///////////////////////////////////////////////
    // FUNCTIONS
    ///////////////////////////////////////////////
    // HANDLE SEARCH SUBMIT KEYBOARD ENTER
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };
    // HANDLE SEARCH INPUT CHANGE
    const handleInputChange = (event) => {
        const searchValue = event.target.value;
        setTyping(true);
        setIsHidden(false);
        setSearchTerm(searchValue);
        setIsOpen(true);
        if (searchValue)
            setSearchActive(true);
        if (searchValue.length === 0) {
            setValue('query', { query: '', restrictToFields: [] });
            setSearchActive(false);
        }
    };
    // HANDLE CLEARING INPUT WITH X BUTTON
    const handleClearInput = () => {
        setValue('query', { query: '', restrictToFields: [] });
        setSearchTerm('');
        setSearchActive(false);
    };
    // HANDLE SEARCH IN FOCUS
    const handleFocus = () => {
        setIsFocused(true);
        setIsHidden(false);
        setIsOpen(true);
    };
    // HANDLE SEARCH OUT OF FOCUS
    const handleBlur = () => {
        setIsFocused(false);
    };
    // HANDLE CLICKING OUTSIDE OF INPUT OR POPOVER
    const handleOnClickAway = () => {
        setIsHidden(true);
    };
    ///////////////////////////////////////////////
    // HOOKS
    ///////////////////////////////////////////////
    useEffect(() => {
        if (results?.aggregations?.index_agg) {
            setFilteredItemsType('aggregate');
            // Incomplete types for aggregations in elastic search
            setFilteredItems((results?.aggregations?.index_agg).buckets);
        }
        else {
            if (results?.hits?.hits) {
                setFilteredItemsType('general');
                setFilteredItems(results.hits.hits);
            }
        }
    }, [results]);
    const handleSearch = debounce(() => {
        if (!initialFormChecked)
            return;
        let searchValue = searchTerm;
        if (showEasyFilter && selectedFilterOption !== 'any_field' && searchTerm) {
            searchValue = `${selectedFilterOption}:${searchTerm}`;
        }
        setValue('query.query', searchValue);
        handleSubmit(onSubmit)();
        setTyping(false);
    }, TIMEOUT);
    useEffect(() => {
        handleSearch();
        return () => handleSearch.cancel();
    }, [searchTerm]);
    // SETS FIRST VALUE FROM DEFAULT FILTERS OR QUERY PARAMS
    useEffect(() => {
        if (formInitialized) {
            if (watchQuery?.query)
                setSearchTerm(watchQuery.query);
            // TIMEOUT NEEDED TO PREVENT CHANGING INITIAL VALUES
            setTimeout(() => {
                setInitialFormChecked(true);
            }, 50);
        }
    }, [formInitialized]);
    // // NEED FOR OPTIONS TO REFRESH
    useEffect(() => {
        // console.log('OPTIONS', options);
    }, [options]);
    ///////////////////////////////////////////////
    // RENDER
    ///////////////////////////////////////////////
    return (_jsx(ClickAwayListener, { onClickAway: handleOnClickAway, children: _jsxs("div", { className: "relative w-full", ref: refs.setReference, ...getReferenceProps(), children: [_jsx(Controller, { name: "query.query", control: control, defaultValue: "", render: ({ field }) => (_jsxs("div", { className: "flex w-full", children: [showEasyFilter && (_jsx(Menu, { buttonProps: {
                                    className: 'h-full',
                                }, button: _jsxs(Button, { className: "btn-base border-blue-gray-200 h-full rounded-l-[7px] rounded-r-none border bg-slate-200 text-xs lowercase", children: [selectedFilterOption, ":"] }), children: easyFilterOptions.map((option) => (_jsxs(MenuItem, { onClick: () => {
                                        setSelectedFilterOptions(option.key);
                                    }, children: [option.key, ":"] }, option.key))) })), _jsx(TextField, { placeholder: placeholder, label: label, error: get(errors), startAdornment: typing && showMessages ? (_jsx(Loading, { size: "h-4 w-4", className: "!text-sourgum-greyblue-400" })) : (_jsx(MagnifyingGlassIcon, { className: `${textFieldDense ? 'size-4' : 'size-5'} text-black ${onMagnifyingGlassClick ? 'cursor-pointer' : ''}`, "aria-hidden": "true", onClick: () => {
                                        if (onMagnifyingGlassClick)
                                            onMagnifyingGlassClick();
                                    } })), endAdornment: searchTerm.length > 0 &&
                                    !hideClearButton && (_jsx(_Fragment, { children: _jsx("div", { onClick: handleClearInput, children: _jsx(XMarkIcon, { className: "size-5 cursor-pointer text-black", "aria-hidden": "true" }) }) })), dense: textFieldDense, inputProps: {
                                    ref: ref,
                                    value: searchTerm,
                                    style: textFieldStyle ? textFieldStyle : { background: 'white' },
                                    onKeyPress: (event) => {
                                        handleKeyPress(event);
                                    },
                                    onChange: (event) => {
                                        field.onChange(event);
                                        handleInputChange(event);
                                    },
                                    onFocus: () => {
                                        handleFocus();
                                    },
                                    onBlur: () => {
                                        handleBlur();
                                    },
                                    className: showEasyFilter ? 'rounded-l-none' : '',
                                } })] })) }), isOpen && (_jsx(Portal, { children: _jsx("div", { className: `absolute z-[400] rounded ${searchPopoverFixed ? '' : 'w-full'}`, ref: refs.setFloating, style: floatingStyles, ...getFloatingProps(), children: _jsx("div", { style: { display: `${isOpen ? 'block' : 'none'}` }, className: `${searchPositionFixed ? 'fixed' : ''}`, children: _jsxs("div", { className: `z-40 my-2 max-h-80 w-full overflow-auto overflow-x-hidden rounded bg-white text-base shadow-lg focus:outline-none`, children: [showTips && isFocused && searchTerm === '' && !typing ? _jsx(SearchTips, {}) : _jsx(_Fragment, {}), showMessages &&
                                        isFocused &&
                                        searchTerm.length > 0 &&
                                        filteredItems.length === 0 &&
                                        isLoading === false &&
                                        !typing ? (_jsx(NoResults, { search: searchTerm })) : (_jsx(_Fragment, {})), showResults && !isHidden && filteredItems.length > 0 && searchTerm.length > 0 && !typing ? (_jsx(SearchResults, { size: options?.resultSize, hits: filteredItems, hitsType: filteredItemsType, search: searchTerm })) : (_jsx(_Fragment, {}))] }) }) }) }))] }) }));
});
SourSearch.displayName = 'SourSearch';
//# sourceMappingURL=search.js.map